import React, { useEffect, useState } from "react";
import { useActionRequest, useDataRequest } from "../../../common/hooks/api";
import { PageLoading } from "../../components";
import { StandardPageWrapper } from "../../components/layout/StandardPageWrapper";
import { usePrimaryOrganization } from "../../hooks";
import { WorkflowSetupPageWrapper } from "./WorkflowSetupPageWrapper";
import { buildStepsFromWorkflowType } from "./HorizontalStepProgressIndicator";
import { WorkflowType } from "../../../common/service/server/dto/RegulationRequestTypeDto";
import { Api } from "../../../common/service/Api";
import { StepNumberCircle, useSnackBar } from "@truevault/aquilo";
import { CustomMessageSampleDto } from "../../../common/service/server/dto/CustomMessageSampleDto";
import { RefreshFn } from "../../../common/hooks/api";
import { InformationCircleIcon } from "@heroicons/react/24/solid";
import { ToolTip } from "@truevault/aquilo";
import { RequestHandlingInstructionsTextarea } from "./RequestHandlingInstructionsTextarea";

export const AppealInstructionsPage = () => {
  const [organization] = usePrimaryOrganization();
  const [appealInstructions, setAppealInstructions] = useState("");

  const { result: fetchedAppealInstructions } = useDataRequest({
    queryKey: ["requestHandlingInstructions", organization?.id, "appeal"],
    api: () => Api.requestHandlingInstructions.getAppealInstructions(organization?.id),
  });

  useEffect(() => {
    setAppealInstructions(fetchedAppealInstructions ?? "");
  }, [fetchedAppealInstructions]);

  const { result: consumerRequestClosedAppealSampleMessage, refresh: refreshConsumerMessage } =
    useDataRequest({
      queryKey: ["emailTemplate", organization?.id, "closed-appeal"],
      api: () => Api.organization.getOrganizationMessageSample(organization?.id, "closed-appeal"),
    });

  if (!organization) {
    return (
      <StandardPageWrapper>
        <PageLoading />
      </StandardPageWrapper>
    );
  }

  const appealStepProgressSteps = buildStepsFromWorkflowType(WorkflowType.APPEAL, 1);
  const handleStepProgressClick = (stepId: string) => {
    document.getElementById(stepId)?.scrollIntoView({ behavior: "smooth" });
  };
  const showCustomTextInputField =
    consumerRequestClosedAppealSampleMessage?.templateHTML?.includes("[[[CUSTOM_TEXT]]]");

  return (
    <WorkflowSetupPageWrapper
      selectedTabKey="appeal-request"
      nextOnClick={async () => {}}
      nextDisabled={false}
      stepProgressSteps={appealStepProgressSteps}
      onStepProgressClick={handleStepProgressClick}
    >
      <div className="tw-boundary">
        <div id="resolve-appeal" className="flex items-center gap-3 mt-10">
          <StepNumberCircle text="1" />
          <div className="text-neutral-900 text-[18px] font-normal">
            Resolve appeal - if a previous privacy request has been found
          </div>
        </div>

        <div className="text-neutral-900 text-[14px] font-normal mt-1.5 ml-[40px]">
          (Optional) Add additional instructions
        </div>
        <RequestHandlingInstructionsTextarea
          rows={4}
          cols={62}
          className="ml-[40px] mt-1.5 block rounded-[5px] bg-white px-3 py-1.5 text-base text-neutral-800 border border-solid border-neutral-400 placeholder:text-neutral-600 focus:outline focus:outline-2 focus:-outline-offset-2 focus:outline-indigo-600"
          placeholder="E.g. find the consumer first at the settings."
          value={appealInstructions}
          onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) =>
            setAppealInstructions(e.target.value)
          }
          onBlur={async (e: React.FocusEvent<HTMLTextAreaElement>) => {
            await Api.requestHandlingInstructions.updateAppealInstructions({
              organizationId: organization.id,
              requestHandlingInstructions: e.target.value,
            });
          }}
        />

        <div id="notify-consumer" className="flex items-center gap-3 mt-10">
          <StepNumberCircle text="2" />
          <div className="text-neutral-900 text-[18px] font-normal">Notify consumer</div>
        </div>

        <div style={{ marginTop: "12px", marginLeft: "40px", marginBottom: "700px" }}>
          <div className="tw-boundary">
            <div className="text-neutral-900 text-[14px]">
              Consumer Email Notification Template (Appeal resolved)
            </div>
          </div>

          <div
            style={{
              backgroundColor: "#F9F8FB",
              padding: "32px",
              maxWidth: "736px",
              marginTop: "6px",
            }}
          >
            <p>
              <strong>To:</strong> <span>consumer@example.com</span>
            </p>

            <p>
              <strong>Subject: A message about your request [REQUEST ID#]</strong>
            </p>

            {showCustomTextInputField ? (
              <AppealEmailTemplateWithCustomTextInputField
                orgId={organization.id}
                consumerRequestSampleMessage={consumerRequestClosedAppealSampleMessage}
                refreshConsumerMessage={refreshConsumerMessage}
              />
            ) : (
              <div
                dangerouslySetInnerHTML={{
                  __html: consumerRequestClosedAppealSampleMessage?.templateHTML,
                }}
              ></div>
            )}
          </div>
        </div>
      </div>
    </WorkflowSetupPageWrapper>
  );
};

function AppealEmailTemplateWithCustomTextInputField({
  orgId,
  refreshConsumerMessage,
  consumerRequestSampleMessage,
}: {
  orgId: string;
  refreshConsumerMessage: RefreshFn;
  consumerRequestSampleMessage: CustomMessageSampleDto;
}) {
  const enqueueSnackbar = useSnackBar();

  const { fetch: updateCustomText } = useActionRequest({
    api: (customMessage) =>
      Api.organization.updateOrganizationMessageSample(
        orgId,
        "closed-appeal",
        customMessage?.trim() ?? "",
      ),
  });

  const [customText, setCustomText] = useState(consumerRequestSampleMessage.customText ?? "");

  const { templateHTML } = consumerRequestSampleMessage;
  const [beforeHTML, afterHTML] = templateHTML
    .split(`<p class="custom-text">[[[CUSTOM_TEXT]]]</p>`)
    .map((t) => t.replace(/href/gi, "xhref"));

  return (
    <div>
      <div dangerouslySetInnerHTML={{ __html: beforeHTML }} />
      <div className="tw-boundary">
        <div className="mb-2 flex items-center gap-2">
          <span className="text-neutral-900 text-sm font-medium">Custom Instructions</span>
          <ToolTip
            text={
              <span>
                Learn more about appeals in our help center.{" "}
                <span className="underline">Click the info icon to open.</span>
              </span>
            }
            width="260px"
            placement="below"
          >
            <a
              href="https://knowledge.truevault.com/article/354-appeals"
              target="_blank"
              rel="noopener noreferrer"
              aria-label="Open appeals help article"
              className="inline-flex items-center"
            >
              <InformationCircleIcon className="w-[16px] h-[16px] text-neutral-500" />
            </a>
          </ToolTip>
        </div>
        <textarea
          rows={4}
          className="block w-full rounded-md bg-white px-3 py-1.5 text-base text-neutral-800 placeholder:text-neutral-600 focus:outline focus:outline-2 focus:-outline-offset-2 focus:outline-indigo-600"
          value={customText}
          placeholder="(optional) Add your own language here"
          onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => {
            setCustomText(e.target.value);
          }}
          onBlur={async (e: React.FocusEvent<HTMLTextAreaElement>) => {
            const newCustomText = e.target.value;
            await updateCustomText(newCustomText);
            await refreshConsumerMessage();
            enqueueSnackbar("Custom text updated", { variant: "success" });
          }}
        />
      </div>
      <div dangerouslySetInnerHTML={{ __html: afterHTML }} />
    </div>
  );
}
